import type { SVGProps } from 'react'

import { type GlyphPath, ICON_STROKE, type IconSize, iconStrokeWidth } from '@gto/icons'

type GlyphPathProps = Pick<
  SVGProps<SVGPathElement>,
  'd' | 'fillRule' | 'fill' | 'stroke' | 'strokeWidth' | 'opacity'
> & {
  'data-part'?: string
}

/** A part with no entry is a line. A part that is filled and stroked takes the stroke of the root. */
const PAINT_PROPS = {
  fill: { fill: 'currentColor', stroke: 'none' },
  both: { fill: 'currentColor' },
} as const

/**
 * The attributes of one `<path>` of a glyph. The root `<svg>` gives the other paint attributes.
 * The part name is a data attribute, not an `id`: a page shows one icon many times.
 *
 * `size` is the `size` of the icon. A part with its own stroke width follows the stroke of
 * the root, so a drawing with two line weights keeps its proportions at each size.
 */
export const glyphPathProps = (
  { d, id, fillRule, paint, strokeWidth, opacity }: GlyphPath,
  size: IconSize | number | undefined,
): GlyphPathProps => ({
  d,
  'data-part': id,
  fillRule,
  opacity,
  strokeWidth:
    strokeWidth === undefined ? undefined : strokeWidth * (iconStrokeWidth(size) / ICON_STROKE),
  ...(paint === undefined ? {} : PAINT_PROPS[paint]),
})
